import { FormControlLabel, FormHelperText, MenuItem, Paper, Radio, Stack, TextField, Typography } from '@mui/material';
import { type FC } from 'react';
import { type Control, Controller, type FieldErrors, useWatch } from 'react-hook-form';

import { carrier, type SimSlot, simSlot } from '@okvc/schema';

import { CARRIER_LABELS, SIM_SLOT_LABELS } from './routerSimForm.js';
import { type RouterFormValues } from '../Forms/formTypes.js';

interface RouterSimFieldsProps {
  control: Control<RouterFormValues>;
  errors?: FieldErrors<RouterFormValues>;
}

interface RouterSimSlotFieldsProps extends RouterSimFieldsProps {
  slot: SimSlot;
}

const RouterSimSlotFields: FC<RouterSimSlotFieldsProps> = ({ control, errors, slot }) => {
  const selectedCarrier = useWatch({ control, name: `sims.${slot}.carrier` });
  const slotErrors = errors?.sims?.[slot];
  const isUnused = !selectedCarrier;

  return (
    <Paper variant={'outlined'} sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography variant={'subtitle2'}>{SIM_SLOT_LABELS[slot]}</Typography>
          <Controller
            name={'primarySlot'}
            control={control}
            render={({ field }) => (
              <FormControlLabel
                label={'Primary'}
                disabled={isUnused}
                control={<Radio checked={field.value === slot} onChange={() => field.onChange(slot)} />}
              />
            )}
          />
        </Stack>
        <Controller
          name={`sims.${slot}.carrier`}
          control={control}
          render={({ field }) => (
            <TextField {...field} select label={'Carrier'}>
              <MenuItem value={''}>Not used</MenuItem>
              {carrier.options.map((option) => (
                <MenuItem key={option} value={option}>{CARRIER_LABELS[option]}</MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          name={`sims.${slot}.iccid`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              disabled={isUnused}
              InputLabelProps={{ required: !isUnused }}
              error={!!slotErrors?.iccid}
              helperText={slotErrors?.iccid?.message ?? ''}
              label={'ICCID'}
            />
          )}
        />
        <Controller
          name={`sims.${slot}.ipAddress`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              disabled={isUnused}
              InputLabelProps={{ required: !isUnused }}
              error={!!slotErrors?.ipAddress}
              helperText={slotErrors?.ipAddress?.message ?? 'The static IP the carrier assigns to this SIM'}
              label={'IP Address'}
            />
          )}
        />
        <Controller
          name={`sims.${slot}.apn`}
          control={control}
          render={({ field }) => <TextField {...field} disabled={isUnused} label={'APN'} />}
        />
      </Stack>
    </Paper>
  );
};

const RouterSimFields: FC<RouterSimFieldsProps> = ({ control, errors }) => (
  <Stack spacing={2}>
    <Typography variant={'subtitle1'}>SIMs</Typography>
    {simSlot.options.map((slot) => (
      <RouterSimSlotFields key={slot} slot={slot} control={control} errors={errors} />
    ))}
    {errors?.primarySlot && <FormHelperText error>{errors.primarySlot.message}</FormHelperText>}
  </Stack>
);

export default RouterSimFields;
